<template>
  <div>
    <!-- Mobile - First time through -->
    <div
      v-if="firstTime"
      class="flex-col text-left"
    >
      <p class="mb-2 text-dark-blue">In which regions do you run?</p>
      <p class="mb-7 text-gray-over-dark">Tap a region to select</p>
      <p class="mb-1 text-left text-2xs font-bold uppercase text-dark-blue">
        US REGIONS
      </p>
      <div class="mt-1">
        <div
          v-for="(region, index) in regionsSVG"
          :key="region.name"
        >
          <div
            class="region-card mb-6"
            :class="{ 'selected-card': regions.includes(region.name) }"
            @click="handleSelect(region.name)"
          >
            <div class="mb-4 flex justify-between">
              <p class="region-header">
                {{ `${region.name} Region` }}
              </p>
              <div
                v-if="regions.includes(region.name)"
                class="flex flex-col rounded-md border border-light-gray p-2"
              >
                <div class="flex">
                  <p class="mr-2 text-2xs font-bold capitalize text-dark-blue">
                    INSPECTIONS
                  </p>
                  <p class="text-2xs font-bold text-body-color">LAST 24 MOS</p>
                </div>
                <div class="my-2 flex items-center">
                  <img
                    src="@/assets/lanes/key_observed.svg"
                    alt="observed key"
                    style="width: 12x; height: 12x"
                  />
                  <p class="mx-2 text-3xs text-body-color">
                    Observed Inspections
                  </p>
                </div>
                <div class="mb-2 flex items-center">
                  <img
                    src="@/assets/lanes/key_preferred.svg"
                    alt="observed key"
                    style="width: 16x; height: 16x"
                  />
                  <p class="mx-2 text-3xs text-body-color">Preferred States</p>
                </div>
                <div class="flex items-center">
                  <img
                    src="@/assets/lanes/key_pref_observed.svg"
                    alt="observed key"
                    style="width: 16x; height: 16x"
                  />
                  <p class="mx-2 text-3xs text-body-color">
                    Preferred + Observed
                  </p>
                </div>
              </div>
              <div
                v-else
                class="flex flex-col rounded-md border border-light-gray p-2"
              >
                <div class="flex">
                  <p class="mr-2 text-2xs font-bold capitalize text-dark-blue">
                    INSPECTIONS
                  </p>
                  <p class="text-2xs font-bold text-body-color">LAST 24 MOS</p>
                </div>
                <div class="mt-2 flex items-center">
                  <img
                    src="@/assets/lanes/key_observed.svg"
                    alt="observed key"
                    style="width: 12x; height: 12x"
                  />
                  <p class="mx-2 text-3xs text-body-color">
                    Observed Inspections
                  </p>
                </div>
              </div>
            </div>
            <div class="mb-4">
              <component
                :is="region.component"
                :key="index"
                class="region-container"
                :unselected="!regions.includes(region.name)"
              />
            </div>
            <radio-button
              :key="region.name"
              class="pointer-events-none"
              :item="region.name"
              :active="regions.includes(region.name)"
              name="regions"
            >
              <template #beforeText> I run in the </template>
              <template #afterText> Region </template>
            </radio-button>
          </div>
        </div>
      </div>
      <p class="text-2xs font-bold text-dark-blue">
        I RUN IN CANADA (REQUIRED)
      </p>
      <div class="mb-6">
        <yes-no
          :field="`lanes.countries.canada`"
          :yes-checked="typeof canada === 'boolean' && canada"
          :no-checked="typeof canada === 'boolean' && !canada"
          :handle-click="handleClickCanada"
          value="'canada'"
        />
      </div>
      <p class="text-2xs font-bold text-dark-blue">
        I RUN IN MEXICO (REQUIRED)
      </p>
      <yes-no
        :field="`lanes.countries.mexico`"
        :yes-checked="typeof mexico === 'boolean' && mexico"
        :no-checked="typeof mexico === 'boolean' && !mexico"
        :handle-click="handleClickMexico"
        value="'mexico'"
      />
    </div>
    <!-- Mobile - Second time through -->
    <div
      v-else-if="!firstTime"
      class="text-left"
    >
      <h4 class="mb-6 mt-1 text-base text-dark-blue">
        Please confirm the regions in which you run.
      </h4>
      <label>US REGIONS</label>
      <p class="mb-4 text-dark-blue">
        {{ runningRegionText }}
      </p>
      <div class="mb-4">
        <us-regional-map :selected-regions="regions" />
      </div>
      <img
        src="@/assets/lanes/key3.svg"
        alt="region key"
        class="mb-6"
      />
      <div class="flex">
        <div class="mr-6">
          <label>RUNS IN CANADA</label>
          <p class="text-dark-blue">
            {{ canada ? 'Yes' : 'No' }}
          </p>
        </div>
        <div>
          <label>RUNS IN MEXICO</label>
          <p class="text-dark-blue">
            {{ mexico ? 'Yes' : 'No' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { shallowRef } from 'vue';
import stepInformation from '@/common/stepInformation';
import RadioButton from '../../../components/RadioButton.vue';
import YesNoRadioButtons from '../../YesNoRadioButtons.vue';
import RoundRadioButton from '../../RoundRadioButton.vue';
import MidwestSvg from '../../../components/steps/Lanes/Regions/Midwest.vue';
import NortheastSvg from '../../../components/steps/Lanes/Regions/Northeast.vue';
import PacificSvg from '../../../components/steps/Lanes/Regions/Pacific.vue';
import RockyMountainSvg from '../../../components/steps/Lanes/Regions/RockyMountain.vue';
import SoutheastSvg from '../../../components/steps/Lanes/Regions/Southeast.vue';
import SouthwestSvg from '../../../components/steps/Lanes/Regions/Southwest.vue';
import USRegionalMapSvg from '../../../components/steps/Lanes/Regions/USRegionalMap.vue';

const midwestSvg = shallowRef(MidwestSvg);
const northeastSvg = shallowRef(NortheastSvg);
const pacificSvg = shallowRef(PacificSvg);
const rockyMountainSvg = shallowRef(RockyMountainSvg);
const southeastSvg = shallowRef(SoutheastSvg);
const southwestSvg = shallowRef(SouthwestSvg);

export default {
  name: 'LanesMobile',
  components: {
    'radio-button': RadioButton,
    'yes-no': YesNoRadioButtons,
    'us-regional-map': USRegionalMapSvg,
    'round-radio-button': RoundRadioButton,
  },
  props: {
    data: Object,
    updateField: Function,
    title: String,
    firstTime: Boolean,
    handleError: Function,
    handleClickCanada: Function,
    handleClickMexico: Function,
    canada: [String, Boolean],
    mexico: [String, Boolean],
    regions: Array,
    addRegion: Function,
    removeRegion: Function,
    inspected: Array,
    preferred: Array,
    preferredByRegion: Object,
    addPreferred: Function,
    removePreferred: Function,
    addPreferredAndRegion: Function,
    removePreferredAndRegion: Function,
    screenSize: String,
    stepName: String,
    updateLanesPacket: Function,
  },
  data() {
    return {
      regionsSVG: [
        { name: 'Midwest', component: midwestSvg },
        { name: 'Northeast', component: northeastSvg },
        { name: 'Pacific', component: pacificSvg },
        { name: 'Rocky Mountain', component: rockyMountainSvg },
        { name: 'Southeast', component: southeastSvg },
        { name: 'Southwest', component: southwestSvg },
      ],
    };
  },
  computed: {
    runningRegionText() {
      let regionsArray = this.regions;
      let arrLen = regionsArray.length;
      let regionText = '';
      switch (regionsArray.length) {
        case 0:
          regionText = 'none of these Regions.';
          break;
        case 1:
          regionText = regionsArray[0] + ' Region.';
          break;
        case 2:
          regionText = regionsArray.join(' and ') + ' Regions.';
          break;
        default:
          regionText =
            regionsArray.slice(0, arrLen - 1).join(', ') +
            ', and ' +
            regionsArray[arrLen - 1] +
            ' Regions.';
          break;
      }
      return `I run in the ${regionText}`;
    },
  },
  methods: {
    handleSelect(value) {
      if (this.regions.includes(value)) {
        this.removeRegion(value);
        stepInformation.lanes.statesByRegion[value].map((el) => {
          this.removePreferred(el);
        });
      } else {
        this.addRegion(value);
        stepInformation.lanes.statesByRegion[value].map((el) => {
          this.addPreferred(el);
        });
      }
      this.handleError('selected', !this.regions.length);
    },
    updateNestedField(path, value) {
      this.$store.commit('updateNestedField', { path, value });
    },
  },
};
</script>

<style scoped>
label {
  font-size: 9px;
  line-height: 13.5px;
  display: block;
  /* text-align: left; */
  @apply mb-1 font-bold text-gray-over-dark;
}

.region-header {
  font-size: 18px;
  line-height: 27px;
  max-width: 145px;
  @apply font-medium text-dark-blue;
}

.region-card {
  padding: 15px 16px;
  border-radius: 5px;
  @apply border-2 border-light-gray;
}

.selected-card {
  @apply border-yellow-500;
}

.region-container {
  max-height: 394px;
  margin: auto;
  margin-bottom: 16px;
}
</style>
